import { getActiveContext } from '@/lib/active-context-store';
import { apiClient } from '@/lib/api-client';
import type { components } from '@/lib/api-types-expenses-suppliers';

export type Supplier = components['schemas']['Supplier'];
export type Site = components['schemas']['Site'];
export type Address = components['schemas']['Address'];
export type Flag = components['schemas']['Flag'];
export type FlagReason = components['schemas']['FlagReason'];
export type VatProposal = components['schemas']['VatProposal'];
export type SupplierUse = components['schemas']['SupplierUse'];
export type HistoryEntry = components['schemas']['HistoryEntry'];
export type SupplierPatch = components['schemas']['SupplierPatch'];
export type SiteInput = components['schemas']['SiteInput'];
/** `updateSupplierSite`: the site's fields, and whether it is still in use. */
export type SitePatch = SiteInput & { active?: boolean };

export const FLAG_REASONS: readonly FlagReason[] = ['closed', 'duplicate', 'wrong_data', 'other'];

/** A page of uses or of history, as the API answers it. */
export type Page<Item> = {
  readonly items: readonly Item[];
  readonly total: number;
  readonly offset: number;
  readonly limit: number;
};

/**
 * What a write gave back: the supplier, or the problem's `code` (and `message`, the person's words
 * when the API has them; `existing`, the other supplier with the same VAT number).
 */
export type WriteOutcome =
  | { readonly ok: true; readonly supplier: Supplier }
  | {
      readonly ok: false;
      readonly status: number;
      readonly code: string | null;
      readonly message: string | null;
      readonly existing: { readonly id: string; readonly name: string } | null;
    };

type Answer = {
  readonly data?: Supplier | undefined;
  readonly error?: unknown;
  readonly response: Response;
};

function problemOf(error: unknown): {
  code: string | null;
  message: string | null;
  existing: { id: string; name: string } | null;
} {
  const body = (typeof error === 'object' && error !== null ? error : {}) as {
    code?: unknown;
    message?: unknown;
    existing?: { id?: unknown; name?: unknown } | null;
  };
  const existing =
    body.existing && typeof body.existing.id === 'string' && typeof body.existing.name === 'string'
      ? { id: body.existing.id, name: body.existing.name }
      : null;
  return {
    code: typeof body.code === 'string' ? body.code : null,
    message: typeof body.message === 'string' ? body.message : null,
    existing,
  };
}

async function settle(call: Promise<Answer>): Promise<WriteOutcome> {
  try {
    const { data, error, response } = await call;
    if (response.ok && data !== undefined) return { ok: true, supplier: data };
    return { ok: false, status: response.status, ...problemOf(error) };
  } catch {
    return { ok: false, status: 0, code: null, message: null, existing: null };
  }
}

const path = (supplierId: string) => ({ supplierId });

/** The place the contract asks for on every call: the active one, as the purposes' calls send it. */
export const header = () => ({
  'X-Active-Tenant-Id': getActiveContext()?.tenantId ?? '',
  'X-Active-Organization-Id': getActiveContext()?.organizationId ?? '',
});

export function updateSupplier(supplier: Supplier, body: SupplierPatch): Promise<WriteOutcome> {
  return settle(
    apiClient.PATCH('/v1/expenses/suppliers/{supplierId}', {
      params: {
        path: path(supplier.id),
        header: { ...header(), 'If-Match': String(supplier.version) },
      },
      body,
    }),
  );
}

/**
 * `addSupplierSite`: anyone in «Note spese» (`can.addSite`); `registeredOffice: true` only for
 * whoever may edit the registry (`can.edit`).
 */
export function addSupplierSite(supplierId: string, site: SiteInput): Promise<WriteOutcome> {
  return settle(
    apiClient.POST('/v1/expenses/suppliers/{supplierId}/sites', {
      params: { path: path(supplierId), header: header() },
      body: site,
    }),
  );
}

/** `updateSupplierSite`: whoever may edit the registry (`can.edit`); a site is never deleted. */
export function updateSite(
  supplierId: string,
  siteId: string,
  body: SitePatch,
): Promise<WriteOutcome> {
  return settle(
    apiClient.PATCH('/v1/expenses/suppliers/{supplierId}/sites/{siteId}', {
      params: { path: { supplierId, siteId }, header: header() },
      body,
    }),
  );
}

export function flagSupplier(
  supplierId: string,
  reason: FlagReason,
  note: string,
): Promise<WriteOutcome> {
  return settle(
    apiClient.POST('/v1/expenses/suppliers/{supplierId}/flags', {
      params: { path: path(supplierId), header: header() },
      body: note === '' ? { reason } : { reason, note },
    }),
  );
}

export function resolveFlag(supplierId: string, flagId: string): Promise<WriteOutcome> {
  return settle(
    apiClient.POST('/v1/expenses/suppliers/{supplierId}/flags/{flagId}/resolve', {
      params: { path: { supplierId, flagId }, header: header() },
      body: {},
    }),
  );
}

export function decideVatProposal(
  supplierId: string,
  proposalId: string,
  decision: 'confirm' | 'reject',
): Promise<WriteOutcome> {
  return settle(
    apiClient.POST('/v1/expenses/suppliers/{supplierId}/vat-proposals/{proposalId}/decision', {
      params: { path: { supplierId, proposalId }, header: header() },
      body: { decision },
    }),
  );
}

export function checkVat(supplierId: string): Promise<WriteOutcome> {
  return settle(
    apiClient.POST('/v1/expenses/suppliers/{supplierId}/vat-check', {
      params: { path: path(supplierId), header: header() },
    }),
  );
}

export function addName(supplierId: string, name: string): Promise<WriteOutcome> {
  return settle(
    apiClient.POST('/v1/expenses/suppliers/{supplierId}/names', {
      params: { path: path(supplierId), header: header() },
      body: { name },
    }),
  );
}

export function removeName(supplierId: string, nameId: string): Promise<WriteOutcome> {
  return settle(
    apiClient.DELETE('/v1/expenses/suppliers/{supplierId}/names/{nameId}', {
      params: { path: { supplierId, nameId }, header: header() },
    }),
  );
}

/** One page of the uses (§3.10: a colleague's rows carry only the month and the category). */
export async function listUses(
  supplierId: string,
  offset: number,
  limit: number,
): Promise<Page<SupplierUse> | null> {
  try {
    const { data, response } = await apiClient.GET('/v1/expenses/suppliers/{supplierId}/uses', {
      params: { path: path(supplierId), header: header(), query: { offset, limit } },
    });
    return response.ok && data !== undefined ? data : null;
  } catch {
    return null;
  }
}

/** One page of «Storia della scheda», newest first. */
export async function listHistory(
  supplierId: string,
  offset: number,
  limit: number,
): Promise<Page<HistoryEntry> | null> {
  try {
    const { data, response } = await apiClient.GET('/v1/expenses/suppliers/{supplierId}/history', {
      params: { path: path(supplierId), header: header(), query: { offset, limit } },
    });
    return response.ok && data !== undefined ? data : null;
  } catch {
    return null;
  }
}
